import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Bounce, toast } from 'react-toastify'

import type { ToggleSource } from '@livekit/components-core'
import { setupMediaToggle, setupManualToggle, log } from '@livekit/components-core'
import { TrackSource } from '@livekit/protocol'
import { ConnectionState, RoomEvent, Track } from 'livekit-client'

import { clientLogError } from '@/src/lib/clientLog'
import { canPublishMicrophone as hasMicrophonePermission, isRoomMicrophoneBlocked } from '@/src/lib/microphonePermissions'

import type { TrackToggleProps } from '@/src/components'
import { Toast } from '@/src/components/Toast/Toast'

import { useMaybeRoomContext } from '@/src/context'
import { ESpecificErrors } from '@/src/constants/constants'

import { mergeProps } from '@/src/mergeProps'

import { useObservableState } from '@/src/hooks/internal'
import { useNavigator } from '@/src/hooks/media/useNavigator'

/** @public */
export interface UseTrackToggleProps<T extends ToggleSource>
  extends Omit<TrackToggleProps<T>, 'showIcon'> {
  isPiP?: boolean
}

interface IScreenShare {
  isPending: boolean
  isEnabled: boolean
}

const initialScreenShare: IScreenShare = {
  isPending: false,
  isEnabled: false,
}

/**
 * The `useTrackToggle` hook is used to implement the `TrackToggle` component and returns state
 * and functionality of the given track.
 *
 * @example
 * ```tsx
 * const { buttonProps, enabled } = useTrackToggle(trackRef);
 * return <button {...buttonProps}>{enabled ? 'disable' : 'enable'}</button>;
 * ```
 * @public
 */
export function useTrackToggle<T extends ToggleSource>({
  source,
  onChange,
  initialState,
  captureOptions,
  isPiP = false,
  skipWarmUp = false,
  publishOptions,
  onDeviceError,
  ...rest
}: UseTrackToggleProps<T>) {
  const { t } = useTranslation()
  const room = useMaybeRoomContext()
  const track = room?.localParticipant?.getTrackPublication(source)
  /** `true` if a user interaction such as a click on the TrackToggle button has occurred. */
  const userInteractionRef = useRef(false)

  const screenStreamRef = useRef<MediaStream | null>(null)
  const [screenShare, setScreenShare] = useState<IScreenShare>(initialScreenShare)
  const localPermissions = room?.localParticipant?.permissions
  const canPublishSources = localPermissions?.canPublishSources
  const canPublishScreenShare =
    !!localPermissions?.canPublish &&
    (!canPublishSources?.length || canPublishSources.includes(TrackSource.SCREEN_SHARE))

  const { toggle, className, pendingObserver, enabledObserver } = useMemo(
    () =>
      room
        ? setupMediaToggle<T>(source, room, captureOptions, publishOptions, onDeviceError)
        : setupManualToggle(),
    [room, source, captureOptions, publishOptions],
  )

  const startScreenShare = useCallback(async () => {
    try {
      setScreenShare((prev) => ({ ...prev, isPending: true }))

      screenStreamRef.current = await navigator?.mediaDevices
        .getDisplayMedia({
          audio: true,
          video: true,
        })
        .then((res) => {
          setScreenShare((prev) => ({ ...prev, isEnabled: true }))
          res?.getTracks()?.forEach((tr) => {
            if (tr?.kind === Track.Kind.Video) {
              room?.localParticipant?.publishTrack(tr, { source: Track.Source.ScreenShare })
            }

            if (tr?.kind === Track.Kind.Audio) {
              room?.localParticipant?.publishTrack(tr, { source: Track.Source.ScreenShareAudio })
            }
          })
          return res
        })
        .catch((err) => {
          setScreenShare((prev) => ({ ...prev, isEnabled: false }))
          console.error('Error with enabling screen sharing: \n', err)
          clientLogError(`Error with enabling screen sharing: ${JSON.stringify(err)}`)
          return null
        })
        .finally(() => setScreenShare((prev) => ({ ...prev, isPending: false })))

      // Обработчик окончания демонстрации пользователем
      screenStreamRef.current
        ?.getVideoTracks()
        ?.at(0)
        ?.addEventListener('ended', () => {
          stopScreenShare()
        })
    } catch (err) {
      console.error('Ошибка при захвате экрана: ', err)
      clientLogError(`Ошибка при захвате экрана: ${JSON.stringify(err)}`)
    }

    return screenStreamRef.current
  }, [room])

  // Функция для остановки демонстрации экрана
  const stopScreenShare = useCallback(() => {
    const screenStream = screenStreamRef.current

    if (screenStream) {
      setScreenShare((prev) => ({ ...prev, isEnabled: false }))
      screenStreamRef.current = null

      // Останавливаем все видеотреки
      screenStream.getTracks().forEach((tr) => {
        room?.localParticipant?.unpublishTrack(tr)
        tr.stop()
      })
    }
  }, [room])

  const pending = useObservableState(pendingObserver, false)
  const enabled = useObservableState(enabledObserver, initialState ?? !!track?.isEnabled)

  const getEnabled = () => (source === Track.Source.ScreenShare ? screenShare?.isEnabled : enabled)
  const getPending = () => (source === Track.Source.ScreenShare ? screenShare?.isPending : pending)

  useEffect(() => {
    if (source === Track.Source.ScreenShare && screenStreamRef.current && !canPublishScreenShare) {
      stopScreenShare()
    }
  }, [canPublishScreenShare, source, stopScreenShare])

  useEffect(() => {
    onChange?.(enabled, userInteractionRef.current)
    userInteractionRef.current = false
  }, [enabled, onChange])

  useEffect(() => {
    if (initialState !== undefined) {
      log.debug('forcing initial toggle state', source, initialState)
      toggle(initialState)
    }
  }, [])

  const { cameraAccess, microphoneAccess } = useNavigator()

  useLayoutEffect(() => {
    if (!room || source !== Track.Source.Microphone || isPiP || skipWarmUp) return

    const warmUpMicrophone = async () => {
      if (isRoomMicrophoneBlocked(room.metadata) || !hasMicrophonePermission(room.localParticipant.permissions)) return
      try {
        await toggle()
        if (hasMicrophonePermission(room.localParticipant.permissions)) {
          await toggle()
        } else {
          await room.localParticipant.setMicrophoneEnabled(false)
        }
      } catch (e) {
        console.error(`Error with toggling Microphone: \n${e}`)
      }
    }

    if (room.state === ConnectionState.Connected) {
      void warmUpMicrophone()
    } else {
      const onConnected = () => {
        void warmUpMicrophone()
        room.off(RoomEvent.Connected, onConnected)
      }
      room.on(RoomEvent.Connected, onConnected)
      return () => {
        room.off(RoomEvent.Connected, onConnected)
      }
    }
  }, [room, source, isPiP, skipWarmUp])

  const newProps = useMemo(() => {
    return mergeProps(rest, { className })
  }, [rest, className])

  const clickHandler: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event: React.MouseEvent) => {
      userInteractionRef.current = true

      // Для ScreenSharing пришлось написать свою реализацию, так как плашка о том, что транслируется вкладка не закрывалась.
      // Это не получилось решить средствами библиотеки livekit, поэтому написал свою реализацию
      if (source === Track.Source.ScreenShare) {
        if (screenStreamRef.current) {
          stopScreenShare()
        } else {
          if (!canPublishScreenShare) {
            toast.error(
              (content) => (
                <Toast title={content?.toastProps?.ariaLabel} description={content?.data} />
              ),
              {
                ariaLabel: t('toast.error'),
                data: t('screenSharePermissionDenied'),
                role: 'alert',
                position: 'bottom-right',
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: 'dark',
                transition: Bounce,
              },
            )
            userInteractionRef.current = false
            return
          }

          await startScreenShare()
        }

        userInteractionRef.current = false
      } else {
        if (source === Track.Source.Microphone && room) {
          if (!hasMicrophonePermission(room.localParticipant.permissions)) {
            toast.error(
              (content) => (
                <Toast title={content?.toastProps?.ariaLabel} description={content?.data} />
              ),
              {
                ariaLabel: t('toast.error'),
                data: t('microphonePermissionDenied'),
                role: 'alert',
                position: 'bottom-right',
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: 'dark',
                transition: Bounce,
              },
            )
            userInteractionRef.current = false
            return
          }
        }

        await toggle()
          .catch((err) => {
            const error: Error = err

            if (source === Track.Source.Camera) {
              const cameraUsedInAnotherApp = error?.message?.includes(
                ESpecificErrors['Camera already used in another app'],
              )

              if (cameraUsedInAnotherApp) {
                toast.error(
                  (content) => (
                    <Toast title={content?.toastProps?.ariaLabel} description={content?.data} />
                  ),
                  {
                    ariaLabel: t('toast.error'),
                    data: t('cameraUsedInAnotherApp'),
                    role: 'alert',
                    position: 'bottom-right',
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                    theme: 'dark',
                    transition: Bounce,
                  },
                )
              }
            }

            return err
          })
          .finally(() => (userInteractionRef.current = false))
      }
    },
    [canPublishScreenShare, room, source, startScreenShare, stopScreenShare, t, toggle],
  )

  // Если у пользователь нет разрешения на использование камеры/микрофона, мы выключаем их
  useLayoutEffect(() => {
    /* 
     Строгая проверка на cameraAccess === false нужна, так как до проверки пермишенов пользователя начальное состояние cameraAccess это null и проверка !cameraAccess в таком случае не подойдет */
    if (cameraAccess === false && source === Track.Source.Camera) {
      toggle(false)
    }

    /* 
     Строгая проверка на microphoneAccess === false нужна, так как до проверки пермишенов пользователя начальное состояние microphoneAccess это null и проверка !microphoneAccess в таком случае не подойдет */
    if (microphoneAccess === false && source === Track.Source.Microphone) {
      toggle(false)
    }
  }, [cameraAccess, microphoneAccess, source])

  return {
    toggle,
    enabled: getEnabled(),
    pending: getPending(),
    track,
    buttonProps: {
      ...newProps,
      'aria-pressed': getEnabled(),
      'data-lk-source': source,
      'data-lk-enabled': getEnabled(),
      disabled: getPending(),
      onClick: clickHandler,
    } as React.ButtonHTMLAttributes<HTMLButtonElement>,
  }
}
